import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { AlertCircle, Check, Copy, Sparkles } from "lucide-react";
import type { AskNexusOpportunitySuggestionCard, AskNexusResolvedSource, AskNexusScriptCard } from "@/features/ask-nexus/api/ask-nexus.api";
import { resolvedSourceText, sceneTime, scriptCopyText } from "@/features/ask-nexus/lib/card-format";
import { ASK_NEXUS_ICON_STROKE } from "@/features/ask-nexus/lib/ui";
import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";

/**
 * A script Ask Nexus wrote, in the prototype's saved-script card. It is a
 * suggestion: the chip says it is not saved in Nexus, its sources are only those
 * Nexus found in this answer's data, and what needs checking is listed apart.
 */
export function AskNexusScript({ card }: { card: AskNexusScriptCard }) {
  const { t } = useTranslation();
  const meta = [
    t("askNexus.cards.script.angle", "Angle: {{text}}", { text: card.angle }),
    ...(card.persona ? [t("askNexus.cards.script.persona", "Persona: {{text}}", { text: card.persona })] : []),
    t("askNexus.cards.script.platform", "Platform: {{text}}", { text: card.platform }),
  ];
  return (
    <SuggestionFrame label={t("askNexus.cards.script.label", "AI suggestion · not saved in Nexus")} testId="ask-nexus-card-script">
      <p className="mt-2.5 text-[15px] font-medium">
        {card.title} · {card.durationSeconds}s
      </p>
      <p className="mt-0.5 text-xs text-(--an-ink-2)">{meta.join(" · ")}</p>
      <div className="mt-3 border-l-2 border-(--an-hair-strong) pl-3 text-[13px] leading-[1.55]">
        {card.scenes.map((scene, index) => (
          <section key={index} className={index > 0 ? "mt-3" : undefined}>
            <PartLabel>{`${sceneTime(scene)} · ${scene.label}`}</PartLabel>
            <p>
              <span className="text-(--an-ink-2)">{t("askNexus.cards.script.visualLabel", "Visual:")}</span> {scene.visual}
            </p>
            {scene.voiceover ? (
              <p>
                <span className="text-(--an-ink-2)">{t("askNexus.cards.script.voiceoverLabel", "Voiceover:")}</span> {scene.voiceover}
              </p>
            ) : null}
            {scene.onScreenText ? (
              <p>
                <span className="text-(--an-ink-2)">{t("askNexus.cards.script.onScreenLabel", "On screen:")}</span> {scene.onScreenText}
              </p>
            ) : null}
          </section>
        ))}
        <section className="mt-3">
          <PartLabel>{t("askNexus.cards.script.ctaLabel", "CTA")}</PartLabel>
          <p>{card.cta}</p>
        </section>
      </div>
      <Sources sources={card.sources} unresolved={card.unresolvedSources} />
      {card.needsConfirmation.length > 0 ? (
        <div className="mt-3 flex items-start gap-2 rounded-xl bg-(--an-inset) px-3 py-2.5 text-xs">
          <AlertCircle className="mt-px size-3.5 flex-none text-(--an-ink-2)" strokeWidth={ASK_NEXUS_ICON_STROKE} aria-hidden="true" />
          <div>
            <p className="font-medium">{t("askNexus.cards.script.checkBeforeUse", "Check before use:")}</p>
            <ul className="mt-0.5 list-disc pl-4 text-(--an-ink-2)">
              {card.needsConfirmation.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}
      <div className="mt-3 flex justify-end">
        <CopyButton text={scriptCopyText(t, card)} label={t("askNexus.cards.script.copy", "Copy script")} />
      </div>
    </SuggestionFrame>
  );
}

/** An opportunity Ask Nexus suggests: not a Nexus record, never scored. */
export function AskNexusOpportunitySuggestion({ card }: { card: AskNexusOpportunitySuggestionCard }) {
  const { t } = useTranslation();
  return (
    <SuggestionFrame label={t("askNexus.cards.suggestion.label", "Suggested by Ask Nexus · not saved")} testId="ask-nexus-card-suggestion">
      <p className="mt-2.5 text-[13px] leading-snug font-medium">{card.title}</p>
      <p className="mt-1 text-[13px] leading-[1.55]">{card.description}</p>
      {card.evidence.length > 0 ? (
        <>
          <p className="mt-3 text-xs font-medium">{t("askNexus.cards.suggestion.why", "Why (Ask Nexus's reading of this answer's data):")}</p>
          <ul className="mt-1 space-y-1 text-xs">
            {card.evidence.map((item) => (
              <li key={item.text}>
                {item.text}
                {item.source ? <span className="block text-(--an-ink-3)">{resolvedSourceText(t, item.source)}</span> : null}
                {item.isSourceMissing ? (
                  <span className="block text-(--an-ink-3)">{t("askNexus.cards.sources.missingOne", "its source isn't in this answer's data")}</span>
                ) : null}
              </li>
            ))}
          </ul>
        </>
      ) : null}
      <p className="mt-3 text-[11px] text-(--an-ink-3)">{t("askNexus.cards.suggestion.noScore", "Not scored: Nexus does not rank suggestions.")}</p>
    </SuggestionFrame>
  );
}

function SuggestionFrame({ label, testId, children }: { label: string; testId: string; children: ReactNode }) {
  return (
    <figure className="min-w-0 rounded-2xl border border-(--an-hair) bg-(--an-field) p-4" data-testid={testId}>
      {/* Grows to a second line on a narrow card instead of spilling out of a fixed-height pill. */}
      <span className="inline-flex max-w-full items-start gap-1.5 rounded-[11px] bg-(--an-neutral-tint) py-[3px] pr-2.5 pl-1.5 text-xs leading-4 font-medium text-balance text-(--an-ink-2)">
        <Sparkles className="mt-0.5 size-3 flex-none" strokeWidth={2.25} aria-hidden="true" />
        {label}
      </span>
      {children}
    </figure>
  );
}

function PartLabel({ children }: { children: string }) {
  return <span className="block text-[10px] font-medium tracking-[0.09em] text-(--an-ink-3) uppercase">{children}</span>;
}

function Sources({ sources, unresolved }: { sources: AskNexusResolvedSource[]; unresolved: number }) {
  const { t } = useTranslation();
  if (sources.length === 0 && unresolved === 0) return null;
  return (
    <div className="mt-3 text-xs">
      <p className="font-medium">{t("askNexus.cards.sources.title", "Sources:")}</p>
      <ul className="mt-0.5 space-y-0.5 text-(--an-ink-2)">
        {sources.map((source, index) => (
          <li key={index}>{resolvedSourceText(t, source)}</li>
        ))}
      </ul>
      {unresolved > 0 ? (
        <p className="mt-0.5 text-(--an-ink-3)">
          {t("askNexus.cards.sources.unresolved", "{{count}} more aren't in this answer's data and aren't shown.", { count: unresolved })}
        </p>
      ) : null}
    </div>
  );
}

function CopyButton({ text, label }: { text: string; label: string }) {
  const { t } = useTranslation();
  const { copied, copy } = useCopyToClipboard();
  const Icon = copied ? Check : Copy;
  return (
    <button
      type="button"
      className="inline-flex h-8 items-center gap-1.5 rounded-full border border-(--an-hair-strong) px-3 text-xs font-medium hover:bg-(--an-inset-hover)"
      onClick={() => void copy(text)}
    >
      <Icon className="size-3.5" strokeWidth={ASK_NEXUS_ICON_STROKE} aria-hidden="true" />
      {copied ? t("askNexus.turn.copied", "Copied") : label}
    </button>
  );
}
